import React from 'react';

import { Card, CardProps, Flex, Loader, RingProgress, Stack, Text } from '@mantine/core';

import { DesignerSubscription, FeatureLimit, FeatureLimits, Subscription, Team, type TeamInfo } from 'models';
import { IntlHelpers } from 'helpers/IntlHelpers';

import { FeatureLimitTrackerLoader } from 'components/feature-limit-tracker/FeatureLimitTracker.Loader';
import { SubscriptionOnboardingButton } from 'components/subscriptions/SubscriptionOnboardingButton';
import { useCheckFeatureLimit } from 'hooks/use-check-feature-limit';
import { useCurrentTeam } from 'hooks/use-current-team';
import { useCurrentTeams } from 'hooks/use-current-teams';
import { usePrices } from 'hooks/use-prices';

type TrackerCopy = {
    remaining: React.ReactNode;
    almostReached: React.ReactNode;
    reached: React.ReactNode;
};

type FeatureLimitTrackerCopy = Record<FeatureLimit, TrackerCopy>;

type Prices = Record<Subscription, { monthly: number; yearly: number }>;

const getNextLimitCopy = (limit: number) =>
    limit === Infinity ? (
        <Text span className="gradient-cyan-green" fw={700}>
            unlimited
        </Text>
    ) : (
        'even more'
    );

const getColor = (remaning: number) => {
    switch (true) {
        case remaning < 10:
            return 'red';
        case remaning < 25:
            return 'orange';
        case remaning < 50:
            return 'yellow';
        default:
            return 'primary';
    }
};

const getCopyKey = (remaning: number): keyof TrackerCopy => {
    switch (true) {
        case remaning <= 0:
            return 'reached';
        case remaning < 25:
            return 'almostReached';
        default:
            return 'remaining';
    }
};

const getCopy = ({
    remaining,
    teams,
    team,
    prices,
}: {
    remaining: number;
    teams: TeamInfo[];
    team: Team;
    prices: Prices;
}): FeatureLimitTrackerCopy => {
    const monthlyPriceLabel = prices[DesignerSubscription.STANDARD].monthly ? (
        <strong>{IntlHelpers.formatPrice(prices[DesignerSubscription.STANDARD].monthly)} / month</strong>
    ) : (
        <strong data-animated-ellipsis>.</strong>
    );

    return {
        [FeatureLimit.PROJECTS]: {
            remaining: (
                <>
                    You have{' '}
                    <b>
                        {remaining} {remaining === 1 ? 'project' : 'projects'}
                    </b>{' '}
                    remaining. Upgrade for {monthlyPriceLabel} to unlock{' '}
                    {getNextLimitCopy(FeatureLimits.PROJECTS[DesignerSubscription.STANDARD])} projects!
                </>
            ),
            almostReached: (
                <>
                    <b>Almost maxed out on projects!</b> Upgrade for {monthlyPriceLabel} to unlock{' '}
                    {getNextLimitCopy(FeatureLimits.PROJECTS[DesignerSubscription.STANDARD])} projects!
                </>
            ),
            reached:
                teams.length > 1 ? (
                    <>
                        <b>Project limit for team {team.name} reached!</b> Upgrade for {monthlyPriceLabel} to unlock{' '}
                        {getNextLimitCopy(FeatureLimits.PROJECTS[DesignerSubscription.STANDARD])} projects!
                    </>
                ) : (
                    <>
                        <b>Project limit reached!</b>
                        <br />
                        Upgrade for {monthlyPriceLabel} to unlock{' '}
                        {getNextLimitCopy(FeatureLimits.PROJECTS[DesignerSubscription.STANDARD])} projects!
                    </>
                ),
        },
        [FeatureLimit.AI_REQUESTS]: {
            remaining: (
                <>
                    You have{' '}
                    <b>
                        {remaining} AI {remaining === 1 ? 'credit' : 'credits'}
                    </b>{' '}
                    remaining. Upgrade for {monthlyPriceLabel} to unlock{' '}
                    {getNextLimitCopy(FeatureLimits.AI_REQUESTS[DesignerSubscription.STANDARD])} AI access!
                </>
            ),
            almostReached: (
                <>
                    <b>Almost maxed out on your AI credits!</b> Upgrade for {monthlyPriceLabel} to unlock{' '}
                    {getNextLimitCopy(FeatureLimits.AI_REQUESTS[DesignerSubscription.STANDARD])} AI access!
                </>
            ),
            reached: (
                <>
                    <b>AI limit reached!</b> Upgrade for {monthlyPriceLabel} to unlock{' '}
                    {getNextLimitCopy(FeatureLimits.AI_REQUESTS[DesignerSubscription.STANDARD])} AI access!
                </>
            ),
        },
        [FeatureLimit.IMAGES]: {
            remaining: (
                <>
                    You have{' '}
                    <b>
                        {remaining} image {remaining === 1 ? 'upload' : 'uploads'}
                    </b>{' '}
                    remaining. Upgrade for {monthlyPriceLabel} to unlock{' '}
                    {getNextLimitCopy(FeatureLimits.IMAGES[DesignerSubscription.STANDARD])} image uploads!
                </>
            ),
            almostReached: (
                <>
                    <b>Almost maxed out on image uploads!</b> Upgrade for {monthlyPriceLabel} to unlock{' '}
                    {getNextLimitCopy(FeatureLimits.IMAGES[DesignerSubscription.STANDARD])} image uploads!
                </>
            ),
            reached: (
                <>
                    <b>Image upload limit reached!</b> Upgrade for {monthlyPriceLabel} to unlock{' '}
                    {getNextLimitCopy(FeatureLimits.IMAGES[DesignerSubscription.STANDARD])} image uploads!
                </>
            ),
        },
        [FeatureLimit.DESIGN_FILE_UPLOADS]: {
            remaining: (
                <>
                    You have{' '}
                    <b>
                        {remaining} design file {remaining === 1 ? 'upload' : 'uploads'}
                    </b>{' '}
                    remaining. Upgrade for {monthlyPriceLabel} to unlock{' '}
                    {getNextLimitCopy(FeatureLimits.DESIGN_FILE_UPLOADS[DesignerSubscription.STANDARD])} uploads!
                </>
            ),
            almostReached: (
                <>
                    <b>Almost maxed out on your design file uploads!</b> Upgrade for {monthlyPriceLabel} to unlock{' '}
                    {getNextLimitCopy(FeatureLimits.DESIGN_FILE_UPLOADS[DesignerSubscription.STANDARD])} uploads!
                </>
            ),
            reached: (
                <>
                    <b>Design file upload limit reached!</b> Upgrade for {monthlyPriceLabel} to unlock{' '}
                    {getNextLimitCopy(FeatureLimits.DESIGN_FILE_UPLOADS[DesignerSubscription.STANDARD])} uploads!
                </>
            ),
        },
    };
};

const DiagramFeatureLimitTracker = ({
    diagramId,
    feature,
    ...props
}: {
    diagramId: string;
    feature: FeatureLimit.IMAGES | FeatureLimit.DESIGN_FILE_UPLOADS;
    onUpgradeClick?: () => void;
} & CardProps) => {
    const { status } = useCheckFeatureLimit(feature, diagramId);

    return <FeatureLimitTrackerBase feature={feature} status={status} {...props} />;
};

const FeatureLimitTracker = ({
    feature,
    showLoader,
    ...props
}: {
    feature: Exclude<FeatureLimit, FeatureLimit.IMAGES | FeatureLimit.DESIGN_FILE_UPLOADS>;
    onUpgradeClick?: () => void;
    showLoader?: 'loader' | 'skeleton' | boolean;
} & CardProps) => {
    const { status } = useCheckFeatureLimit(feature);

    return <FeatureLimitTrackerBase feature={feature} status={status} {...props} />;
};

const FeatureLimitTrackerBase = ({
    feature,
    status,
    onUpgradeClick,
    showLoader = 'loader',
    ...props
}: {
    feature: FeatureLimit;
    status: {
        count: number;
        limit: number;
    } | null;
    onUpgradeClick?: () => void;
    showLoader?: 'loader' | 'skeleton' | boolean;
} & CardProps) => {
    const team = useCurrentTeam();
    const teams = useCurrentTeams();

    const { prices } = usePrices();

    if (!team) return null;

    if (!status) {
        if (!showLoader) return null;

        return showLoader === 'skeleton' ? (
            <FeatureLimitTrackerLoader />
        ) : showLoader === 'loader' ? (
            <Card w="100%" p="xs" {...props}>
                <Loader size="sm" color="primary" mx="auto" my="auto" />
            </Card>
        ) : null;
    }

    const { limit, count } = status;

    const remaining = Math.max(limit - count, 0);

    const remainingPercentage = (remaining / limit) * 100;
    const color = getColor(remainingPercentage);

    const copy = getCopy({
        remaining,
        teams,
        team,
        prices,
    })[feature];
    const copyKey = getCopyKey(remainingPercentage);

    if (limit === 9999) return null; // 9999 = unlimited

    return (
        <Card w="100%" p="xs" {...props}>
            <Flex h="100%" gap="xs" align="center">
                <RingProgress
                    roundCaps
                    size={80}
                    thickness={6}
                    sections={[{ value: remainingPercentage, color }]}
                    rootColor={`${color}.1`}
                    label={
                        <Stack align="center" ta="center" gap={0}>
                            <Text fz={10} c="dimmed">
                                Left
                            </Text>
                            <Text fz="sm" fw={800} c={color} mt={-4}>
                                {remaining}/{limit}
                            </Text>
                        </Stack>
                    }
                />

                <Stack align="flex-start" gap={8}>
                    <Text>{copy[copyKey]}</Text>

                    <SubscriptionOnboardingButton size="xs" onClick={onUpgradeClick}>
                        Upgrade to Plus
                    </SubscriptionOnboardingButton>
                </Stack>
            </Flex>
        </Card>
    );
};

export { FeatureLimitTracker, DiagramFeatureLimitTracker };
